import { Role } from '@common/enums/request.enums'
import { TraineeCertification } from '@common/types/certification.types'
import Icon from '@components/dataDisplay/Icon'
import Chip from '@components/layout/Chip'
import Loader from '@components/state/Loader'
import {
	GREY_COLORS,
	PARKER_GOLDEN_COLORS,
	RED_COLORS
} from '@config/theme/colors'
import {
	FontSize,
	FontWeight
} from '@config/theme/sizes'
import { Download } from '@mui/icons-material'
import {
	Stack,
	styled,
	Typography
} from '@mui/material'
import { useCertificationApi } from '@services/api/certification.api'
import { useAuth } from '@services/contexts/AuthContext'
import { useMutation } from '@tanstack/react-query'
import { isBefore } from 'date-fns'
import {
	Dispatch,
	SetStateAction
} from 'react'
import { FormattedMessage } from 'react-intl'

type CertificationProps = {
	certification: TraineeCertification
	setSelectedCertification: Dispatch<SetStateAction<TraineeCertification | undefined>>
	traineeId: string | undefined
}

const CertificationRow = (
	{
		certification,
		setSelectedCertification
	}: CertificationProps
) => {
	const { user } = useAuth()
	const { downloadCertification } = useCertificationApi()

	const isUpdatable = user.role === Role.ADMIN || !certification.isTrainer

	const { mutate: downloadCertificationMutate, isPending } = useMutation({
		mutationKey: ['downloadCertification'],
		mutationFn: () => downloadCertification(certification.id)
	})

	return (
		<Certification onClick={() => isUpdatable ? setSelectedCertification(certification) : undefined} isUpdatable={isUpdatable}>
			<FirstRow>
				<Typography fontWeight={FontWeight.MEDIUM}>
					<FormattedMessage id={`global.enums.certificationCourse.${certification.course}`} />
				</Typography>
				{certification.isTrainer && (
					<Chip color={GREY_COLORS}>
						<FormattedMessage id="trainee.certifications.list.trainer" />
					</Chip>
				)}
				<Typography color={GREY_COLORS[500]}>
					{`/ ${certification.certificationNumber}`}
				</Typography>
			</FirstRow>
			<SecondRow>
				<Typography color={GREY_COLORS[500]} variant="body2">
					<FormattedMessage
						id="trainee.certifications.list.training"
						values={{
							trainer: certification.trainerName,
							date: new Intl.DateTimeFormat().format(new Date(certification.trainedOn)),
							company: certification.companyName
						}}
					/>
				</Typography>
				<Chip color={PARKER_GOLDEN_COLORS}>
					<FormattedMessage id={`global.enums.companyType.${certification.companyType}`} />
				</Chip>
			</SecondRow>
			<ThirdRow>
				<Typography color={isBefore(new Date(certification.expiryDate), new Date()) ? RED_COLORS[300] : GREY_COLORS[500]} variant="body2">
					<FormattedMessage
						id="trainee.certifications.list.expiringDate"
						values={{ date: new Intl.DateTimeFormat().format(new Date(certification.expiryDate)) }}
					/>
				</Typography>
				{
					isPending
						? (
							<LoaderContainer>
								<Loader />
							</LoaderContainer>
						)
						: (
							<DownloadButton
								className="download"
								onClick={(event) => {
									event.stopPropagation()
									downloadCertificationMutate()
								}}
							>
								<Typography fontSize={FontSize.XXS} fontWeight={FontWeight.MEDIUM}>
									<FormattedMessage id="trainee.certifications.list.download.label" />
								</Typography>
								<Icon IconElement={Download} size="16px" />
							</DownloadButton>
						)
				}
			</ThirdRow>
		</Certification>
	)
}

const Certification = styled(
	Stack,
	{ shouldForwardProp: (prop) => prop !== 'isUpdatable' }
)<{ isUpdatable: boolean }>(({ isUpdatable }) => ({
	cursor: isUpdatable ? 'pointer' : 'default',
	padding: '8px 12px',
	rowGap: '8px',
	'&:hover': {
		backgroundColor: isUpdatable ? GREY_COLORS[50] : 'auto',
		'& .download': {
			opacity: '1'
		}
	}
}))

const FirstRow = styled(Stack)({
	alignItems: 'center',
	columnGap: '8px',
	flexDirection: 'row'
})

const SecondRow = styled(Stack)({
	alignItems: 'center',
	columnGap: '8px',
	flexDirection: 'row'
})

const ThirdRow = styled(Stack)({
	alignItems: 'center',
	flexDirection: 'row',
	justifyContent: 'space-between',
	position: 'relative'
})

const LoaderContainer = styled(Stack)({
	alignItems: 'center',
	border: `1px solid transparent`,
	height: '16px',
	padding: '4px 8px'
})

const DownloadButton = styled(Stack)({
	alignItems: 'center',
	border: `1px solid ${GREY_COLORS[200]}`,
	borderRadius: '8px',
	columnGap: '4px',
	height: '16px',
	flexDirection: 'row',
	opacity: 0,
	padding: '4px 8px'
})

export default CertificationRow
